/**
 * Actualización de términos y política (pantalla D del diseño aprobado el
 * 25-sep-2026).
 *
 * `OnboardingGate` manda aquí al funcionario que ya terminó el wizard pero
 * no ha aceptado la versión vigente (`legal_pending` no vacío). Dice en
 * lenguaje simple qué cambió, pide las mismas dos casillas que el
 * onboarding y siempre ofrece salida: quien no acepta puede cerrar sesión y
 * escribirnos. Si solo cierra la pestaña, vuelve a ver esta pantalla en el
 * próximo ingreso.
 *
 * Vive fuera de `OnboardingGate` (mismo patrón que `/membresia/renovar`): el
 * gate lo redirigiría aquí mismo.
 */
import { useMutation } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router-dom";

import { LegalConsent } from "@/components/legal/LegalConsent";
import { LEGAL_CHANGES, LEGAL_VIGENTE_DESDE, type LegalDocId } from "@/legal/documentos";
import { legalConsentErrorMessage } from "@/legal/errores";
import { RESPONSABLE } from "@/legal/responsable";
import { acceptLegalDocuments } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useMunicipality } from "@/lib/municipality";

export function ActualizacionLegal() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { context, isLoading, needsOnboarding, refetch } = useMunicipality();

  const mutation = useMutation({
    mutationFn: (versions: Record<LegalDocId, string>) => acceptLegalDocuments(versions),
    onSuccess: async () => {
      await refetch();
      navigate("/radar", { replace: true });
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <p className="text-sm text-slate-500">Cargando…</p>
      </div>
    );
  }

  // Quien no terminó el wizard acepta en su último paso, no aquí.
  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }
  // Nada pendiente (ya aceptó, o entró por la URL directa): al Radar.
  if (!context?.legal_pending?.length && !mutation.isPending) {
    return <Navigate to="/radar" replace />;
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface p-4 sm:p-6">
      <div className="w-full max-w-xl rounded-3xl border border-slate-200 bg-white p-5 shadow-md sm:p-8">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600">
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
              <path d="M12 3L4 8l8 5 8-5-8-5z" fill="white" opacity="0.9" />
              <path d="M4 13l8 5 8-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </span>
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">
            Actualizado el {LEGAL_VIGENTE_DESDE}
          </span>
        </div>

        <h1 className="text-xl font-bold text-balance text-slate-900">
          Actualizamos nuestros términos y la política de privacidad
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Para seguir usando Kaptar necesitamos que revises y aceptes la nueva versión. Te tomará un
          minuto.
        </p>

        <ul className="my-5 flex flex-col gap-2.5" data-testid="legal-changes">
          {LEGAL_CHANGES.map((change) => (
            <li
              key={change.text}
              className="flex gap-2.5 border-b border-slate-100 pb-2.5 text-sm text-slate-700 last:border-0 last:pb-0"
            >
              <span className="mt-0.5 h-fit shrink-0 rounded-md bg-brand-50 px-1.5 py-0.5 text-[11px] font-bold tracking-wide text-brand-700 uppercase">
                {change.kind}
              </span>
              <span>{change.text}</span>
            </li>
          ))}
        </ul>

        <LegalConsent
          submitLabel="Aceptar y continuar"
          pendingLabel="Guardando tu aceptación…"
          submitTestId="legal-accept-submit"
          isPending={mutation.isPending}
          error={mutation.isError ? legalConsentErrorMessage(mutation.error) : null}
          onSubmit={(versions) => mutation.mutate(versions)}
        />

        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-500">
          <span>¿No estás de acuerdo? Escríbenos a {RESPONSABLE.correoDatos}</span>
          <button
            type="button"
            data-testid="legal-sign-out"
            onClick={async () => {
              await signOut();
              navigate("/login", { replace: true });
            }}
            className="p-1 font-semibold text-slate-600 underline hover:text-slate-900"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </main>
  );
}
